import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { redirectToCheckout } from "../../utils/checkout";

const money = (amount, currency) => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  const code = String(currency || "").trim().toUpperCase();
  const value = Number.isInteger(n) ? String(n) : n.toFixed(2);
  return code ? `${code} ${value}` : value;
};

// Render a single limitViolation defensively — it may be a string or an object
// with varying field names.
const violationText = (v) => {
  if (typeof v === "string") return v;
  if (v?.message) return v.message;
  const resource = v?.resource || v?.name || v?.limit || v?.field || "Limit";
  const current = v?.current ?? v?.used ?? v?.count;
  const allowed = v?.allowed ?? v?.max ?? v?.limitValue ?? v?.value;
  if (current != null && allowed != null) {
    return `${resource}: ${current} in use, new plan allows ${allowed}`;
  }
  return `${resource} exceeds the selected plan's limit`;
};

// Two modes:
//  - "payment": upgrade proration / downgrade-with-debt -> shows the breakdown,
//    then redirects to authorizationUrl on confirm.
//  - "blocked": DOWNGRADE_BLOCKED -> lists limitViolations, no payment.
const PlanChangeDialog = ({ open, onClose, mode, payment, blocked }) => {
  if (mode === "blocked") {
    const violations = blocked?.limitViolations || [];
    return (
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Downgrade not available</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "14px", color: "#555", mb: 1.5 }}>
            {blocked?.message ||
              "Your current usage exceeds the limits of the selected plan. Reduce usage in these areas before downgrading:"}
          </Typography>
          {violations.length > 0 && (
            <List dense disablePadding>
              {violations.map((v, i) => (
                <ListItem key={i} disableGutters sx={{ alignItems: "flex-start" }}>
                  <ListItemIcon sx={{ minWidth: 30, mt: 0.5 }}>
                    <ErrorOutlineIcon color="error" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{ fontSize: "13px", color: "#202020" }}
                    primary={violationText(v)}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} sx={{ textTransform: "none" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // payment mode
  const data = payment || {};
  const currency =
    data?.newPlan?.currency ||
    data?.outstanding?.currency ||
    data?.currentPlan?.currency ||
    "";
  const breakdown = data?.paymentBreakdown;
  // Prefer the explicit paymentBreakdown (upgrade); fall back to outstanding +
  // payable (downgrade-with-debt).
  const outstandingAmount =
    breakdown?.outstandingAmount ?? data?.outstanding?.outstandingAmount ?? 0;
  const prorationAmount =
    breakdown?.prorationAmount ?? data?.proration?.payable ?? 0;
  const totalPayable = breakdown?.totalPayable ?? data?.payable ?? 0;
  const credit = data?.proration?.credit;

  const Row = ({ label, value, bold }) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        py: 0.75,
      }}
    >
      <Typography sx={{ fontSize: "13px", fontWeight: bold ? 700 : 400, color: "#202020" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "13px", fontWeight: bold ? 700 : 500, color: "#202020" }}>
        {value}
      </Typography>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>
        {data?.changeType === "upgrade" ? "Confirm upgrade" : "Complete payment"}
      </DialogTitle>
      <DialogContent>
        {data?.message && (
          <Typography sx={{ fontSize: "13px", color: "#555", mb: 1.5 }}>
            {data.message}
          </Typography>
        )}

        {(data?.currentPlan || data?.newPlan) && (
          <Typography sx={{ fontSize: "13px", color: "#202020", mb: 1 }}>
            {data?.currentPlan?.planName || "Current"} →{" "}
            <strong>{data?.newPlan?.planName || "New plan"}</strong>
          </Typography>
        )}

        <Divider sx={{ my: 1 }} />

        {Number(outstandingAmount) > 0 && (
          <Row label="Outstanding balance" value={money(outstandingAmount, currency)} />
        )}
        <Row label="Prorated amount" value={money(prorationAmount, currency)} />
        {Number(credit) > 0 && (
          <Row label="Credit" value={`− ${money(credit, currency)}`} />
        )}
        <Divider sx={{ my: 0.5 }} />
        <Row label="Total payable" value={money(totalPayable, currency)} bold />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => redirectToCheckout(data?.authorizationUrl)}
          sx={{ textTransform: "none", backgroundColor: "#000" }}
        >
          Proceed to payment
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlanChangeDialog;
